import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS, Dropdown } from "components";
import { TYRE_POSITIONS } from "models";
import "modules/CarDetails/components/Tyres/components/DBBLabelsModal/DBBLabelsModal.scss";
import { getDBBComment, getPositionInitialsDutch, handlePrint, shortenText } from "modules/CarDetails/components/Tyres/components/DBBLabelsModal/utils";
import { useCarDealer } from "modules/CarDetails/hooks";
import { CAR_DETAILS_STORAGE_KEYS } from "modules/CarDetails/storageKeys";
import { PRINT_SIZE, getPreference, setPreference } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { Appointment, Car, DealerLocation } from "models";
import type { DBBLabel } from "modules/CarDetails/components/Tyres/components/DBBLabelsModal/utils";

type DBBModalProps = {
  tyreLabels: DBBLabel[];
  location?: DealerLocation;
  appointment?: Appointment;
  car?: Car;
  onClose: () => void;
  open: boolean;
};

export const DBBLabelsModal = ({ open, onClose, tyreLabels, appointment, car, location }: DBBModalProps) => {
  const { t } = useTranslation();

  const [printSize, setPrintSize] = useState(getPreference(CAR_DETAILS_STORAGE_KEYS.TyreLabelPrintSizePreference, PRINT_SIZE.A4));

  const dealer = useCarDealer(car);

  if (!car) return null;

  const onPaperSizeChange = (size?: PRINT_SIZE) => {
    if (!size) return;

    setPrintSize(size);
    setPreference(CAR_DETAILS_STORAGE_KEYS.TyreLabelPrintSizePreference, size);
  };

  return (
    <Modal className="DBBLabelsModal" closeOnEscape size="large" dimmer="blurring" open={open} onClose={onClose} closeOnDimmerClick={false}>
      <Modal.Actions>
        <div className="dbb_label_dropdown">
          <span>{t("v8_select_paper_size", "Select paper size")}</span>
          <Dropdown
            selection
            options={[
              { key: PRINT_SIZE.A4, text: "A4", value: PRINT_SIZE.A4 },
              { key: PRINT_SIZE.Label, text: "90mm", value: PRINT_SIZE.Label }
            ]}
            value={printSize}
            placeholder={t("v8_select_paper_size", "Select paper size")}
            onChange={onPaperSizeChange}
          />
        </div>
        <div className="dbb_actions">
          <Button color="green" onClick={() => handlePrint()}>
            {t("v8_print", "PRINT")}
          </Button>
          <Button color="green" onClick={onClose}>
            {t("v8_close", "CLOSE")}
          </Button>
        </div>
      </Modal.Actions>
      <Modal.Content scrolling id={"dbb_label_content_container"}>
        <div className={`dbb_wrapper ${printSize === PRINT_SIZE.Label ? "dbb_wrapper_label_size" : ""}`}>
          {tyreLabels.map((item, i) => {
            const hasTyreSpecificationsInDescription = item.tyre.description.includes("R") && item.tyre.description.includes("/");

            return (
              <div className={`dbb_label_box ${printSize === PRINT_SIZE.Label ? "dbb_label_box_label_size" : ""}`} key={i}>
                <h1 className={`${printSize === PRINT_SIZE.Label ? "h1_label_size" : ""}`}>{car.reg_nr_escaped}</h1>
                <div className={`dbb_label_content ${printSize === PRINT_SIZE.Label ? "dbb_label_content_label_size" : ""}`}>
                  <div className="dbb_label_tyre">
                    <div className="dbb_label_tyre_position">
                      <span>{getPositionInitialsDutch(item.position)}</span>:
                    </div>
                    <div className="dbb_label_tyre_size">{item.profile.toFixed(1)}mm</div>
                    <div className="dbb_label_tyre_description">
                      {item.tyre.manufacturer} {shortenText(printSize, item.tyre.description, hasTyreSpecificationsInDescription ? 32 : 24)}
                      {!hasTyreSpecificationsInDescription && ` ${item.tyre.width}/${item.tyre.height}R${item.tyre.size} ${item.tyre.speed}`}
                    </div>
                  </div>
                  <div className={`dbb_label_comment ${printSize === PRINT_SIZE.Label ? "mb-28_label_size" : ""}`}>
                    <span className="dbb_label_name">Opm:</span>
                    <span className="float-right">
                      {shortenText(printSize, getDBBComment(item.position, item.position > TYRE_POSITIONS.CarLast ? car.in_storage_tyres : car.on_car_tyres) || "", 80)}
                    </span>
                  </div>
                  <div className={`dbb_label_section mb-26 ${printSize === PRINT_SIZE.Label ? "mb-26_label_size" : ""}`}>
                    <div>
                      <span className="dbb_label_name">Dealer: </span>
                      <span className="float-right">{shortenText(printSize, dealer?.name, 35)}</span>
                    </div>
                    <div>
                      <span className="dbb_label_name">Plaats: </span>
                      <span className="float-right">{shortenText(printSize, location?.name, 35)}</span>
                    </div>
                    <div>
                      <span className="dbb_label_name">Eigenaar:</span>
                      <span className="float-right">
                        {shortenText(printSize, `${appointment?.owner_title} ${appointment?.owner_firstname} ${appointment?.owner_surname}`, 33)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="dbb_label_name">Berijder:</span>
                    <span className="float-right">
                      {shortenText(printSize, `${appointment?.driver_title} ${appointment?.driver_firstname} ${appointment?.driver_surname}`, 35)}
                    </span>
                  </div>
                  <div className={`mb-24 ${printSize === PRINT_SIZE.Label ? "mb-24_label_size" : ""}`}>
                    <span className="dbb_label_name">{t("v8_appointment_date", "Appointment Date")}:</span>
                    <span className="float-right">{formatDate(appointment?.time_car_app, DATE_FORMATS_FNS.dateMonthYear)}</span>
                  </div>
                  <div>
                    <span className="dbb_label_name">Opslaglocatie: </span>
                    <div className="dbb_location_name float-right">
                      {car.in_storage_tyres && (
                        <>
                          {shortenText(printSize, car.in_storage_tyres.dbb_location_name, 28)}
                          <br />
                          {car.in_storage_tyres.dbb_sub_location_name && `(${shortenText(printSize, car.in_storage_tyres.dbb_sub_location_name, 27)})`}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Modal.Content>
    </Modal>
  );
};
